import { Component, computed, input, output, signal } from '@angular/core';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
})
export class UploadComponent {
  label = input('Arraste o arquivo aqui ou ');
  icon = input('backup');
  maxSize = input('2 MB');
  recommendedSize = input('624 x 350');
  acceptedFiles = input(['jpg', 'png']);
  fileSelected = output<File | null>();
  selectedFile = signal<File | null>(null);
  uploadProgress = signal<number>(0);

  acceptedFileTypes = computed(() =>
    this.acceptedFiles()
      .map(ext => `.${ext}`)
      .join(','),
  );

  private convertSizeToBytes(sizeString: string): number {
    const units = {
      B: 1,
      KB: 1024,
      MB: 1024 * 1024,
      GB: 1024 * 1024 * 1024,
    };

    const [size, unit] = sizeString.split(' ');
    return parseFloat(size) * units[unit as keyof typeof units];
  }

  private calculateProgress(fileSize: number, maxSizeInBytes: number): number {
    const percentage = (fileSize / maxSizeInBytes) * 100;
    return Math.min(percentage, 100); // Limita a 100% mesmo se o arquivo for maior
  }

  getFileIcon(file: File | null): string {
    if (!file) return 'pi pi-image';

    const extension = file.name.split('.').pop()?.toLowerCase();

    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
        return 'pi pi-image text-xl';
      default:
        return 'pi pi-file text-xl';
    }
  }

  async onImageUrlChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const url = input.value;

    if (url) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const filename = url.split('/').pop() || 'image.jpg';
        const file = new File([blob], filename, { type: blob.type });
        const maxSizeInBytes = this.convertSizeToBytes(this.maxSize());
        const progress = this.calculateProgress(file.size, maxSizeInBytes);
        this.uploadProgress.set(progress);
        this.selectedFile.set(file);
        this.fileSelected.emit(file);
      } catch (error) {
        console.error('Error loading image from URL:', error);
      }
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      const maxSizeInBytes = this.convertSizeToBytes(this.maxSize());
      const progress = this.calculateProgress(file.size, maxSizeInBytes);
      this.uploadProgress.set(progress);
      this.selectedFile.set(file);
      this.fileSelected.emit(file);
    }
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files?.length) {
      const file = files[0];
      const maxSizeInBytes = this.convertSizeToBytes(this.maxSize());
      const progress = this.calculateProgress(file.size, maxSizeInBytes);
      this.uploadProgress.set(progress);
      this.selectedFile.set(file);
      this.fileSelected.emit(file);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  clearFile(event: Event) {
    event.stopPropagation();
    this.selectedFile.set(null);
    this.uploadProgress.set(0);
    this.fileSelected.emit(null);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
